import { useState } from "react";

import { countryApi, manufacturerApi, modelApi, subModelApi, yearApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { KeyValueDefaultEnum } from "@/main/models/common/keyValueDefaultEnum";
import {
	CountryByVehicleFilterRequest,
	SubModelRequest,
	VehicleFiltersRequest,
	VehicleYearDomainRequest,
	VehicleYearRequest,
} from "@/main/models/request";
import { isNullUndefined } from "@/main/services/utils";

function mapCountriesToOptions(countries: KeyValueDefaultEnum[] | null): KeyValue[] | null {
	if (isNullUndefined(countries)) return null;

	return countries.map((country) => ({
		key: country.key,
		value: country.value,
	}));
}

function useVehicleFilterOptions() {
	const [countries, setCountries] = useState<KeyValue[] | null>(null);
	const [manufacturers, setManufacturers] = useState<KeyValue[] | null>(null);
	const [models, setModels] = useState<KeyValue[] | null>(null);
	const [subModels, setSubModels] = useState<KeyValue[] | null>(null);
	const [years, setYears] = useState<KeyValue[] | null>(null);
	const [isLoadingCountry, setIsLoadingCountry] = useState(false);
	const [isLoadingManufacturer, setIsLoadingManufacturer] = useState(false);
	const [isLoadingModel, setIsLoadingModel] = useState(false);
	const [isLoadingSubModel, setIsLoadingSubModel] = useState(false);
	const [isLoadingYear, setIsLoadingYear] = useState(false);

	function loadCountries(filter?: CountryByVehicleFilterRequest) {
		setIsLoadingCountry(true);

		countryApi
			.getDomainByVehicles(filter)
			.then((response) => {
				const { result } = response.data;
				setCountries(mapCountriesToOptions(result));
			})
			.finally(() => {
				setIsLoadingCountry(false);
			});
	}

	function loadManufacturers(filter: VehicleFiltersRequest) {
		setIsLoadingManufacturer(true);

		manufacturerApi
			.getVehicleManufacturerByFilters(filter)
			.then((response) => {
				const { result } = response.data;
				setManufacturers(result ?? null);
			})
			.finally(() => {
				setIsLoadingManufacturer(false);
			});
	}

	function loadModels(filter: VehicleFiltersRequest) {
		setIsLoadingModel(true);

		modelApi
			.getVehicleModelByFilters(filter)
			.then((response) => {
				const { result } = response.data;
				setModels(result ?? null);
			})
			.finally(() => {
				setIsLoadingModel(false);
			});
	}

	function loadSubModels(filter: SubModelRequest) {
		setIsLoadingSubModel(true);

		subModelApi
			.getVehicleSubModel(filter)
			.then((response) => {
				const { result } = response.data;
				setSubModels(result ?? null);
			})
			.finally(() => {
				setIsLoadingSubModel(false);
			});
	}

	function loadYears(filter: VehicleYearRequest) {
		setIsLoadingYear(true);

		yearApi
			.getVehicleYearByManufacturerAndModel(filter)
			.then((response) => {
				const { result } = response.data;
				setYears(result ?? null);
			})
			.finally(() => {
				setIsLoadingYear(false);
			});
	}

	function loadYearsDomain(filter?: VehicleYearDomainRequest) {
		setIsLoadingYear(true);

		yearApi
			.getVehicleYear(filter)
			.then((response) => {
				const { result } = response.data;
				setYears(result ?? null);
			})
			.finally(() => {
				setIsLoadingYear(false);
			});
	}

	function clearManufacturers() {
		setManufacturers(null);
	}

	function clearModels() {
		setModels(null);
	}

	function clearSubModels() {
		setSubModels(null);
	}

	function clearYears() {
		setYears(null);
	}

	return {
		countries,
		manufacturers,
		models,
		subModels,
		years,
		isLoadingCountry,
		isLoadingManufacturer,
		isLoadingModel,
		isLoadingSubModel,
		isLoadingYear,
		loadCountries,
		loadManufacturers,
		loadModels,
		loadSubModels,
		loadYears,
		loadYearsDomain,
		clearManufacturers,
		clearModels,
		clearSubModels,
		clearYears,
	};
}

export default useVehicleFilterOptions;
